import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { MatTable, MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { SaveService } from '../../services/save.service';
import { DefaultValuesService } from "../../services/defaultvalues.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { KpiProductHierarchyMappingService } from "./kpiproducthierarchy-mapping.service";
import { CreateKpiProductHierarchyPopupComponent } from "./kpiproducthierarchy-popup/kpiproducthierarchy-popup.component";
import { ToastrService } from "ngx-toastr";
import { KpiProductHierarchyService } from "./kpiproducthierarchy-master.service";
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';

@Component({
    selector: "kpiproducthierarchy-mapping",
    standalone: true,
    imports: [
        MatTableModule,
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        ReactiveFormsModule,
        FormsModule,
        MatTooltipModule,
        MatProgressBarModule
    ],
    templateUrl: './kpiproducthierarchy-mapping.component.html',
    styleUrls: [
        '../../style/mat-table.component.less',
        '../../style/dialogue-box.less',
        '../../style/tables/table-add-butt-header-orange.less',
        '../../style/new-generic-styles.component.less',
        './kpiproducthierarchy-mapping.component.less'
    ]
})

export class KpiProductHierarchyMappingComponent implements OnInit, OnDestroy {
    @ViewChild(MatTable, { static: false }) table!: MatTable<KpiProductHierarchyMapping>;

    public dataSource = new MatTableDataSource<KpiProductHierarchyMapping>();
    columnsToDisplay = ['TenantId', 'ManagementId', 'OperationalSystem', 'LocalProductName', 'GlobalProductName', 'actions'];

    managementIds: { value: string; label: string }[] = [];
    operationalSystems: OperationalSystem[] = [];
    globalProducts: GlobalProductRef[] = [];

    selectedManagementId: string = '';
    selectedOperationalSystem: string = '';

    allMappings: KpiProductHierarchyMapping[] = [];
    originalMappings: KpiProductHierarchyMapping[] = [];

    filterString: string = '';
    
    protected _onDestroy = new Subject<void>();

    constructor(
        private readonly saveService: SaveService,
        private readonly defaults: DefaultValuesService,
        private readonly matDialog: MatDialog,
        private readonly kpiProductHierarchyMappingService: KpiProductHierarchyMappingService,
        private readonly kpiProductHierarchyService: KpiProductHierarchyService,
        private readonly toastrService: ToastrService
    ) {}

    ngOnInit() {
        this.getHeader();
        this.getProductsMapping();
        this.loadOperationalSystems();
        this.loadGlobalProducts();
        this.saveService.addListener(this);

        // The tenant scope comes from the global tenant selector at the top of the
        // page. When it changes, reload the mapping list for the new tenant.
        this.defaults.tenantMessageChanges$
            .pipe(takeUntil(this._onDestroy))
            .subscribe(() => {
                this.selectedManagementId = '';
                this.selectedOperationalSystem = '';
                this.filterString = '';
                this.getProductsMapping();
            });

        this.dataSource.filterPredicate = (data: KpiProductHierarchyMapping, filter: string) => {
            const unmappedOnly = filter === 'unmapped';
            if (unmappedOnly) {
                return !data.GlobalProductName || data.GlobalProductName.trim() === '';
            }
            return true;
        };
    }

    ngOnDestroy() {
        this.saveService.removeListener(this);
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    getProductsMapping() {
        this.kpiProductHierarchyMappingService.getAllKpiProductsHierarchyMapping().subscribe({
            next: (data: KpiProductHierarchyMapping[]) => {
                this.allMappings = data.map(m => ({ ...m, isEditable: true }));
                this.originalMappings = structuredClone(data);
                this.dataSource.data = this.allMappings;
                this.buildFilterOptions();
            }
        });
    }

    // Build the filter option lists from the loaded data so the values always
    // match what is being filtered on (fixes tenant / management id filters).
    buildFilterOptions() {
        const seen = new Map<string, string>();
        for (const m of this.allMappings) {
            const id = m.ManagementId?.toString();
            if (id !== null && id !== undefined && id !== '' && !seen.has(id)) {
                // Show the country code (TenantId) as the label, keep the
                // ManagementId as the filter value.
                seen.set(id, m.TenantId ?? id);
            }
        }

        this.managementIds = [...seen.entries()]
            .map(([value, label]) => ({ value, label }))
            .sort((a, b) => a.label.localeCompare(b.label));

        // Derive operational systems from the loaded data so the filter values
        // always match what is stored (fixes operational system filter).
        this.operationalSystems = [...new Set(this.allMappings
            .map(m => m.OperationalSystem)
            .filter((v): v is string => v !== null && v !== undefined && v !== ''))]
            .sort((a, b) => a.localeCompare(b))
            .map(v => ({ SystemId: v, SystemName: v }));
    }

    getHeader() {
        if (this.defaults.isReadonlyUser)
            this.columnsToDisplay = ['TenantId', 'ManagementId', 'OperationalSystem', 'LocalProductName', 'GlobalProductName'];
        else
            this.columnsToDisplay = ['TenantId', 'ManagementId', 'OperationalSystem', 'LocalProductName', 'GlobalProductName', 'actions'];
    }

    loadOperationalSystems() {
        // Operational systems are derived from the loaded mapping data in
        // buildFilterOptions() so the filter values always match the data.
    }

    loadGlobalProducts() {
        this.kpiProductHierarchyService.getAllKpiProductsHierarchy().subscribe({
            next: (data: KpiProductHierarchy[]) => {
                this.globalProducts = data.map(p => ({
                    KpiProductHierarchyId: p.KpiProductHierarchyId,
                    ProductDescription: p.ProductHierarchyDescription,
                }));
            }
        });
    }

    onRowEditInit(productMapping: KpiProductHierarchyMapping) {
        const mappingIndex = this.dataSource.data.findIndex(x => 
            this.getDictionaryHash(x) === this.getDictionaryHash(productMapping)
        );
        
        if (mappingIndex === -1) {
            console.error('Product mapping not found in dataSource');
            return;
        }

        this.dataSource.data[mappingIndex].isEditable = false;
        this.saveService.addListener(this);
        this.dataSource.data = [...this.dataSource.data];
    }

    isEditingRow(element: KpiProductHierarchyMapping): boolean {
        return element.isEditable === false;
    }

    isAnyRowEditing(): boolean {
        return this.dataSource.data.some(x => x.isEditable === false);
    }

    onRowEditCancel(productMapping: KpiProductHierarchyMapping) {
        const mappingIndex = this.dataSource.data.findIndex(x => 
            this.getDictionaryHash(x) === this.getDictionaryHash(productMapping)
        );
        
        if (mappingIndex === -1) {
            console.error('Product mapping not found in dataSource');
            return;
        }

        const original = this.originalMappings.find(m => 
            this.getDictionaryHash(m) === this.getDictionaryHash(productMapping)
        );
        
        if (original) {
            this.dataSource.data[mappingIndex].GlobalProductName = original.GlobalProductName;
            this.dataSource.data[mappingIndex].IsMapped = !original.GlobalProductName;
        }
        
        this.dataSource.data[mappingIndex].isEditable = true;
        
        const hasEditableRows = this.dataSource.data.some(x => x.isEditable === false);
        if (!hasEditableRows) {
            this.saveService.showSaveButton(false);
        }
        
        this.dataSource.data = [...this.dataSource.data];
    }

    changeGlobalProduct(value: any, element: KpiProductHierarchyMapping) {
        element.GlobalProductName = value.value;
        element.IsMapped = !!value.value;
    }

    filterUnmapped() {
        this.dataSource.filter = 'unmapped';
    }

    onChangeOperationalSystem() {
        this.applyFilter();
    }

    onChangeManagementId() {
        this.applyFilter();
    }

    clearFilter() {
        this.dataSource.filter = '';
        this.filterString = '';
        this.selectedManagementId = '';
        this.selectedOperationalSystem = '';
        this.applyFilter();
    }

    applyTextFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.filterString = filterValue.trim().toLowerCase();
        this.applyFilter();
    }
    
    applyFilter(filterValue?: string) {
        if (filterValue !== undefined) {
            this.filterString = filterValue.trim().toLowerCase();
        }
        
        let filteredData = [...this.allMappings];

        if (this.selectedManagementId) {
            filteredData = filteredData.filter(m => m.ManagementId?.toString() === this.selectedManagementId);
        }

        if (this.selectedOperationalSystem) {
            filteredData = filteredData.filter(m => m.OperationalSystem === this.selectedOperationalSystem);
        }
        
        if (this.filterString) {
            filteredData = filteredData.filter((x: KpiProductHierarchyMapping) =>
                x.LocalProductName?.toLowerCase().includes(this.filterString) ||
                x.OperationalSystem?.toLowerCase().includes(this.filterString) ||
                x.ManagementId?.toString().toLowerCase().includes(this.filterString) ||
                x.TenantId?.toLowerCase().includes(this.filterString) ||
                x.GlobalProductName?.toLowerCase().includes(this.filterString)
            );
        }
        
        this.dataSource.data = filteredData;
    }

    onSave() {
        const mappingsToSave: KpiProductHierarchyMapping[] = [];
        const editedRows = this.dataSource.data.filter(x => x.isEditable === false);
        
        for (let currentMapping of editedRows) {
            const original = this.originalMappings.find(m => 
                this.getDictionaryHash(m) === this.getDictionaryHash(currentMapping)
            );
            if (original) {
                if (currentMapping.GlobalProductName !== original.GlobalProductName) {
                    mappingsToSave.push(currentMapping);
                }
            }
        }

        if (mappingsToSave.length === 0) {
            this.dataSource.data.forEach(x => x.isEditable = true);
            this.dataSource.data = [...this.dataSource.data];
            return;
        }

        this.dataSource.data.forEach(x => x.isEditable = true);
        this.dataSource.data = [...this.dataSource.data]

        this.kpiProductHierarchyMappingService.updateKpiProductHierarchyMapping(mappingsToSave).subscribe({
            next: () => {
                this.toastrService.success(`${mappingsToSave.length} mappings saved successfully`);
                this.getProductsMapping();
            }
        });
    }

    onGetHistory(element: KpiProductHierarchyMapping) {
        this.matDialog.open(CreateKpiProductHierarchyPopupComponent, {
            panelClass: ["product-hierarchy-dialogue", "custom-mat-dialog_v2"],
            width: '1000px',
            maxWidth: '90vw',
            data: { action: "history", productMappingSelected: element.KpiProductHierarchyMappingId }
        });
    }

    getDictionaryHash(productMapping: KpiProductHierarchyMapping): string {
        return `${productMapping.KpiProductHierarchyMappingId}`;
    }

    isUnmapped(element: KpiProductHierarchyMapping): boolean {
        return !element.GlobalProductName || element.GlobalProductName.trim() === '';
    }
}